import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore/lite";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, useModals } from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import type { ImageInput } from "senf-atomic-design-system/src/components/special/imageModifier/ImagesUploadModal";
import ImagesUploadModal from "senf-atomic-design-system/src/components/special/imageModifier/ImagesUploadModal";
import { db, storage } from "senf-shared";
import { geminiRequest } from "../../lib/geminiRequest";
import { initializeModelsInFirestore } from "../utils/uploadInitialModels";

interface Model {
  id: string;
  title: {
    de: string;
    en: string;
  };
  prompt: string;
  keywords: {
    de: string[];
    en: string[];
  };
  imgURL: string | null;
  strength?: number;
  category: string;
  model: string;
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
  title: {
    en: string;
    de: string;
  };
  index: number;
}

interface AddModelModalProps {
  onSubmit: (model: Model) => void;
  categories: Category[];
  handleGemini: (
    type: "translate" | "generatePrompt" | "generateKeywords" | "matchCategory",
    value: string,
    model: Model,
    customModelUpdater?: (model: Model) => void,
  ) => Promise<void>;
}

const AddModelModal: React.FC<AddModelModalProps> = ({
  onSubmit,
  categories,
  handleGemini,
}) => {
  const { closeModal } = useModals();
  const [englishTitle, setEnglishTitle] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSubmit = async () => {
    if (!englishTitle.trim()) return;

    setIsGenerating(true);
    try {
      // Create initial model with temporary values
      const tempModel: Model = {
        id: `temp-${Date.now()}`,
        title: {
          en: englishTitle,
          de: "",
        },
        prompt: "",
        keywords: {
          en: [],
          de: [],
        },
        imgURL: null,
        category: "",
        model: englishTitle.toLowerCase().replace(/\s+/g, "_"),
        createdAt: new Date().toISOString(),
      };

      // Instead of using Promise.all, we'll do these sequentially to ensure each update is captured
      let currentModel = { ...tempModel };

      // Translate
      await handleGemini(
        "translate",
        englishTitle,
        currentModel,
        (updatedModel) => {
          currentModel = { ...updatedModel };
        },
      );

      // Generate prompt
      await handleGemini(
        "generatePrompt",
        englishTitle,
        currentModel,
        (updatedModel) => {
          currentModel = { ...updatedModel };
        },
      );

      // Generate keywords
      await handleGemini(
        "generateKeywords",
        englishTitle,
        currentModel,
        (updatedModel) => {
          currentModel = { ...updatedModel };
        },
      );

      // Match category - do this last to ensure we have all the information
      await handleGemini(
        "matchCategory",
        englishTitle,
        currentModel,
        (updatedModel) => {
          currentModel = { ...updatedModel };
        },
      );

      // Ensure category is set
      if (!currentModel.category && categories.length > 0) {
        currentModel.category = categories[0].id;
      }

      onSubmit(currentModel);
      closeModal();
    } catch (error) {
      console.error("Error generating model content:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">Add New Model</h2>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          English Title
        </label>
        <Input
          type="text"
          value={englishTitle}
          onChange={(e) => setEnglishTitle(e.target.value)}
          placeholder="Enter English title"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={() => closeModal()} variant="outline" text="Cancel" />
        <Button
          onClick={handleSubmit}
          variant="primary"
          text={isGenerating ? "Generating..." : "Create"}
          disabled={!englishTitle.trim() || isGenerating}
        />
      </div>
    </div>
  );
};

// Add the getImgFetch function before the ObjectsPage component
const getImgFetch = async (prompt: string) => {
  const options = {
    method: "POST",
    headers: {
      Authorization: `Bearer ${import.meta.env.VITE_GETIMG_ACCESS_KEY}`,
      accept: "application/json",
      "content-type": "application/json",
    },
    body: JSON.stringify({
      prompt,
      width: 512,
      height: 512,
      steps: 4,
      seed: 0,
      output_format: "jpeg",
    }),
  };

  return fetch("https://api.getimg.ai/v1/flux-schnell/text-to-image", options)
    .then((response) => response.json())
    .then((response) => {
      return response.image;
    })
    .catch((err) => {
      console.error(err);
      return null;
    });
};

const ObjectsPage: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [originalModels, setOriginalModels] = useState<Model[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [originalCategories, setOriginalCategories] = useState<Category[]>([]);
  const [activeTab, setActiveTab] = useState<"models" | "categories">("models");
  const { openModal } = useModals();
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { t } = useTranslation();

  // Track if there are unsaved changes
  const hasChanges = useMemo(() => {
    const modelsChanged =
      JSON.stringify(models) !== JSON.stringify(originalModels);
    const categoriesChanged =
      JSON.stringify(categories) !== JSON.stringify(originalCategories);
    return modelsChanged || categoriesChanged;
  }, [models, originalModels, categories, originalCategories]);

  // Add filtered models computation
  const filteredModels = useMemo(() => {
    if (!selectedCategory) return models;
    return models.filter((model) => model.category === selectedCategory);
  }, [models, selectedCategory]);

  useEffect(() => {
    fetchModels();
    fetchCategories();
  }, []);

  const fetchModels = async () => {
    try {
      const utopiaDoc = doc(db, "utopia", "general");
      const modelsCollection = collection(utopiaDoc, "models");
      const modelsSnapshot = await getDocs(modelsCollection);
      const modelsList = modelsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Model[];
      setModels(modelsList);
      setOriginalModels(modelsList);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const utopiaDoc = doc(db, "utopia", "general");
      const categoriesCollection = collection(utopiaDoc, "categories");
      const categoriesSnapshot = await getDocs(categoriesCollection);
      const categoriesList = categoriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
      const sortedCategories = categoriesList.sort((a, b) => a.index - b.index);
      setCategories(sortedCategories);
      setOriginalCategories(sortedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleModelDelete = async (modelId: string) => {
    try {
      setDeletingIds((prev) => [...prev, modelId]);
      const utopiaDoc = doc(db, "utopia", "general");
      const modelsCollection = collection(utopiaDoc, "models");
      const modelRef = doc(modelsCollection, modelId);

      // Get the model data first
      const model = models.find((m) => m.id === modelId);

      await deleteDoc(modelRef);

      // Delete the image if it exists
      if (model?.imgURL) {
        const imageRef = ref(storage, `utopia/models/${modelId}`);
        await deleteObject(imageRef);
      }

      await fetchModels();
    } catch (error) {
      console.error("Error deleting model:", error);
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== modelId));
    }
  };

  const handleImageUpload = async (file: File, modelId: string) => {
    try {
      const imageRef = ref(storage, `utopia/models/${modelId}`);
      await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(imageRef);
      return imageUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      return null;
    }
  };

  const handleCategoryDelete = async (categoryId: string) => {
    try {
      setDeletingIds((prev) => [...prev, categoryId]);
      const utopiaDoc = doc(db, "utopia", "general");
      const categoriesCollection = collection(utopiaDoc, "categories");
      const categoryRef = doc(categoriesCollection, categoryId);
      await deleteDoc(categoryRef);
      await fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== categoryId));
    }
  };

  const handleSaveAll = async () => {
    try {
      const utopiaDoc = doc(db, "utopia", "general");

      // Save models changes
      const modelsCollection = collection(utopiaDoc, "models");
      for (const model of models) {
        if (model.id.startsWith("temp-")) {
          // New model
          const { id, ...modelData } = model;
          await addDoc(modelsCollection, modelData);
        } else {
          // Update existing model
          const modelRef = doc(modelsCollection, model.id);
          const { id, ...modelData } = model;
          await updateDoc(modelRef, modelData);
        }
      }

      // Save categories changes
      const categoriesCollection = collection(utopiaDoc, "categories");
      for (const category of categories) {
        if (category.id.startsWith("temp-")) {
          // New category
          const { id, ...categoryData } = category;
          await addDoc(categoriesCollection, categoryData);
        } else {
          // Update existing category
          const categoryRef = doc(categoriesCollection, category.id);
          const { id, ...categoryData } = category;
          await updateDoc(categoryRef, categoryData);
        }
      }

      // Refresh data to get new IDs
      await fetchModels();
      await fetchCategories();
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleDownloadJSON = () => {
    const data = {
      models,
      categories,
    };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `models-categories-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportJSON = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const text = await file.text();
        const data = JSON.parse(text);

        const utopiaDoc = doc(db, "utopia", "general");

        if (data.models && Array.isArray(data.models)) {
          for (const model of data.models) {
            const modelsCollection = collection(utopiaDoc, "models");
            const { id, ...modelData } = model;
            await addDoc(modelsCollection, modelData);
          }
        }

        if (data.categories && Array.isArray(data.categories)) {
          for (const category of data.categories) {
            const categoriesCollection = collection(utopiaDoc, "categories");
            const { id, ...categoryData } = category;
            await addDoc(categoriesCollection, categoryData);
          }
        }
        await fetchModels();
        await fetchCategories();
      } catch (error) {
        console.error("Error importing JSON:", error);
        alert(t("admin.errors.failedImportJson"));
      }
    };
    input.click();
  };

  const handleModelChange = (updatedModel: Model) => {
    setModels((prevModels) =>
      prevModels.map((model) =>
        model.id === updatedModel.id ? updatedModel : model,
      ),
    );
  };

  const handleCategoryChange = (updatedCategory: Category) => {
    setCategories((prevCategories) =>
      prevCategories.map((category) =>
        category.id === updatedCategory.id ? updatedCategory : category,
      ),
    );
  };

  const handleGemini = async (
    type: "translate" | "generatePrompt" | "generateKeywords" | "matchCategory",
    value: string,
    model: Model,
    customModelUpdater?: (model: Model) => void,
  ) => {
    try {
      let prompt = "";
      if (type === "translate") {
        prompt = `Translate this text to German: "${value}"`;
      } else if (type === "generatePrompt") {
        prompt = `Generate a detailed image generation prompt for this urban design element: "${value}".
                 Focus on specific visual details like materials, textures, colors, and form.
                 The prompt should be 5-10 words and describe a standalone object that could be added to existing city scenes.
                 Emphasize photorealistic qualities and architectural/design elements.
                 Format: Modern [object] with [key visual details]. Avoid describing surroundings or context.
                 Example: "Modern bench with sleek brushed steel frame, ergonomic wooden slats, and subtle LED accent lighting underneath. Clean minimalist design with gentle curves."`;
      } else if (type === "generateKeywords") {
        prompt = `Generate relevant keywords for this urban design element: "${value}". 
                 Return a JSON object with two arrays of keywords:
                 {
                   "en": ["keyword1", "keyword2", "keyword3"],
                   "de": ["keyword1", "keyword2", "keyword3"]
                 }
                 English keywords in "en" array, German translations in "de" array.
                 Include 4-6 relevant keywords that describe the functionality, appearance, and purpose.`;
      } else if (type === "matchCategory") {
        const categoryOptions = categories.map((cat) => ({
          id: cat.id,
          title: cat.title.en,
          description: `${cat.title.en} - urban design elements related to ${cat.title.en.toLowerCase()}`,
        }));

        prompt = `Given this urban design element: "${value}", determine which category it best fits into.
                 Here are the available categories:
                 ${JSON.stringify(categoryOptions, null, 2)}
                 
                 Return only the ID of the best matching category as a plain string, without any additional text or formatting.
                 Consider the purpose, functionality, and characteristics of the element when matching.
                 If you're not sure, return the ID that seems most appropriate.
                 The response should contain ONLY the category ID, nothing else.`;
      }

      const response = await geminiRequest({
        body: prompt,
        lang: type === "translate" ? "de" : "en",
        assistantId: type === "generateKeywords" ? "jsonDefault" : "default",
      });

      if (response) {
        const updatedModel = { ...model };
        if (type === "translate") {
          updatedModel.title.de = response;
        } else if (type === "generatePrompt") {
          updatedModel.prompt = response;
          // Generate image based on the prompt
          try {
            const base64Image = await getImgFetch(
              `${response}. Hyper-realistic, modern`,
            );
            if (base64Image) {
              updatedModel.imgURL = `data:image/jpeg;base64,${base64Image}`;
            }
          } catch (error) {
            console.error("Error generating image:", error);
          }
        } else if (type === "generateKeywords") {
          try {
            const keywords = JSON.parse(response);
            if (keywords.en && keywords.de) {
              updatedModel.keywords = keywords;
            }
          } catch (error) {
            console.error("Error parsing keywords JSON:", error);
          }
        } else if (type === "matchCategory") {
          // Clean up the response to get just the ID
          const categoryId = response.trim().replace(/['"]/g, "");

          // Check if this is a valid category ID
          const matchedCategory = categories.find(
            (cat) => cat.id === categoryId,
          );

          if (matchedCategory) {
            updatedModel.category = categoryId;
            console.log(
              `Matched category: ${matchedCategory.title.en} (${categoryId})`,
            );
          } else {
            console.warn(
              `No matching category found for ID: ${categoryId}. Available categories:`,
              categories.map((c) => `${c.id}: ${c.title.en}`),
            );
            // Only set default category if no category is set
            if (!updatedModel.category && categories.length > 0) {
              updatedModel.category = categories[0].id;
              console.log(`Using default category: ${categories[0].title.en}`);
            }
          }
        }
        if (customModelUpdater) {
          customModelUpdater(updatedModel);
        } else {
          handleModelChange(updatedModel);
        }
      }
    } catch (error) {
      console.error("Error with Gemini request:", error);
    }
  };

  const handleAddNewModel = () => {
    if (activeTab === "models") {
      openModal(
        <AddModelModal
          onSubmit={async (newModel) => {
            try {
              // First add to Firestore to get a real ID
              const utopiaDoc = doc(db, "utopia", "general");
              const modelsCollection = collection(utopiaDoc, "models");

              // Remove the temp ID before saving
              const { id, ...modelData } = newModel;

              // Add to Firestore
              const docRef = await addDoc(modelsCollection, modelData);

              // Create the final model with the real Firestore ID
              const finalModel = {
                ...newModel,
                id: docRef.id,
              };

              // Update local state
              setModels((prevModels) => [...prevModels, finalModel]);

              console.log("New model saved to DB with ID:", docRef.id);
            } catch (error) {
              console.error("Error saving new model to DB:", error);
            }
          }}
          categories={categories}
          handleGemini={handleGemini}
        />,
        { size: "md" },
      );
    } else {
      const maxIndex =
        categories.length > 0
          ? Math.max(...categories.map((c) => c.index))
          : -1;

      const newCategory: Category = {
        id: `temp-${Date.now()}`,
        name: "",
        title: { en: "", de: "" },
        index: maxIndex + 1,
      };
      setCategories((prevCategories) => [...prevCategories, newCategory]);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-8 fixed top-0 bg-white w-screen left-0 p-3 z-50 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">Utopia Admin</h1>

          <div className="flex space-x-4 mt-3">
            <button
              className={`px-4 py-2 ${
                activeTab === "models"
                  ? "border-b-2 border-border-highlight-default"
                  : ""
              }`}
              onClick={() => setActiveTab("models")}
            >
              Models
            </button>
            <button
              className={`px-4 py-2 ${
                activeTab === "categories"
                  ? "border-b-2 border-border-highlight-default"
                  : ""
              }`}
              onClick={() => setActiveTab("categories")}
            >
              Categories
            </button>
          </div>

          {/* <Button
            onClick={handleInitializeModels}
            disabled={isInitializing}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:bg-gray-400"
          >
            {isInitializing ? 'Initializing...' : 'Initialize Models from Models.ts'}
          </Button> */}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadJSON}
            variant="outline"
            text="Download JSON"
          />
          <Button
            onClick={handleImportJSON}
            variant="outline"
            text="Import JSON"
          />
          <Button
            onClick={handleSaveAll}
            disabled={!hasChanges}
            variant="primary"
            size="lg"
            text="Save Changes"
          />
        </div>
      </div>

      <div className="w-full mx-auto px-4 pb-8 mt-20">
        <div className="mb-4 flex justify-end" />

        <div className="mb-4 flex justify-end" />

        <div className="flex gap-2 w-full">
          <div className="flex gap-2 w-auto flex-col  sticky top-[90px] h-fit">
            <div className="flex gap-2 flex-col border rounded-xl p-2 bg-white  h-fit min-w-[230px]">
              <Button
                onClick={() => setSelectedCategory(null)}
                text="All Categories"
                size="sm"
                variant={selectedCategory === null ? "primary" : "outline"}
              />
              {categories.length > 0 &&
                categories.map((category) => (
                  <div key={category.id}>
                    <Button
                      onClick={() => setSelectedCategory(category.id)}
                      text={category.title.en}
                      size="sm"
                      variant={
                        selectedCategory === category.id ? "primary" : "outline"
                      }
                    />
                  </div>
                ))}
            </div>
            <Button
              onClick={handleAddNewModel}
              text={`Add New ${activeTab === "models" ? "Model" : "Category"}`}
              size="lg"
              variant="primary"
              leadingIcon="+"
            />
          </div>

          <div className="overflow-x-auto border rounded-xl min-w-[1200px] ">
            {activeTab === "models" ? (
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2">Image</th>
                    <th className="px-4 py-2">Title (EN)</th>
                    <th className="px-4 py-2">Title (DE)</th>
                    <th className="px-4 py-2">Prompt</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModels.map((model) => (
                    <tr key={model.id} className="border-t">
                      <td className="px-4 py-2 w-24">
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                        <div
                          onClick={() =>
                            openModal(
                              <ImagesUploadModal
                                onInputChange={async (files: ImageInput[]) => {
                                  if (files[0] instanceof File) {
                                    const imageUrl = await handleImageUpload(
                                      files[0],
                                      model.id,
                                    );
                                    if (imageUrl) {
                                      handleModelChange({
                                        ...model,
                                        imgURL: imageUrl,
                                      });
                                    }
                                  } else if ("url" in files[0]) {
                                    // Handle stock/AI url selection
                                    handleModelChange({
                                      ...model,
                                      imgURL: files[0].url,
                                    });
                                  }
                                }}
                                maxItems={1}
                                fileTypes={[
                                  "image/jpg",
                                  "image/jpeg",
                                  "image/png",
                                  "image/webp",
                                ]}
                                stock
                                ai
                                basePath="utopia/models"
                              />,
                              { size: "xl" },
                            )
                          }
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          {model.imgURL ? (
                            <img
                              src={model.imgURL}
                              alt={model.title.en}
                              className="w-20 h-20 object-cover rounded"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center text-sm text-gray-500">
                              Click to add image
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-2">
                          <Input
                            type="text"
                            value={model.title.en}
                            onChange={(e) => {
                              const updatedModel = {
                                ...model,
                                title: { ...model.title, en: e.target.value },
                              };
                              handleModelChange(updatedModel);
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="text"
                          value={model.title.de}
                          onChange={(e) => {
                            const updatedModel = {
                              ...model,
                              title: { ...model.title, de: e.target.value },
                            };
                            handleModelChange(updatedModel);
                          }}
                          trailingIcon={
                            <Button
                              onClick={() =>
                                handleGemini("translate", model.title.en, model)
                              }
                              size="sm"
                              variant="outline"
                              leadingIcon="*"
                              className="mt-1"
                            />
                          }
                        />
                      </td>
                      <td className="px-4 py-2 min-w-[400px]">
                        <div className="flex flex-col gap-2">
                          <Input
                            type="textarea"
                            value={model.prompt}
                            onChange={(e) => {
                              const updatedModel = {
                                ...model,
                                prompt: e.target.value,
                              };
                              handleModelChange(updatedModel);
                            }}
                            maxRows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              onClick={() =>
                                handleGemini(
                                  "generatePrompt",
                                  model.title.en,
                                  model,
                                )
                              }
                              size="sm"
                              leadingIcon="ai"
                              className="mt-1"
                            >
                              Generate Prompt
                            </Button>
                            <Button
                              onClick={() =>
                                handleGemini(
                                  "generateKeywords",
                                  model.title.en,
                                  model,
                                )
                              }
                              size="sm"
                              leadingIcon="ai"
                              className="mt-1"
                            >
                              Generate Keywords
                            </Button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          value={model.category}
                          onChange={(e) => {
                            const updatedModel = {
                              ...model,
                              category: e.target.value,
                            };
                            handleModelChange(updatedModel);
                          }}
                          className="w-full p-2 border rounded"
                        >
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.title.en}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleModelDelete(model.id)}
                            disabled={deletingIds.includes(model.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              deletingIds.includes(model.id)
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-red-500 hover:bg-red-600"
                            } text-white`}
                          >
                            {deletingIds.includes(model.id)
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Title (EN)</th>
                    <th className="px-4 py-2">Title (DE)</th>
                    <th className="px-4 py-2">Index</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories
                    .sort((a, b) => a.index - b.index)
                    .map((category) => (
                      <tr key={category.id} className="border-t">
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={category.name}
                            onChange={(e) => {
                              const updatedCategory = {
                                ...category,
                                name: e.target.value,
                              };
                              handleCategoryChange(updatedCategory);
                            }}
                            className="w-full p-2 border rounded"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={category.title.en}
                            onChange={(e) => {
                              const updatedCategory = {
                                ...category,
                                title: {
                                  ...category.title,
                                  en: e.target.value,
                                },
                              };
                              handleCategoryChange(updatedCategory);
                            }}
                            className="w-full p-2 border rounded"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={category.title.de}
                            onChange={(e) => {
                              const updatedCategory = {
                                ...category,
                                title: {
                                  ...category.title,
                                  de: e.target.value,
                                },
                              };
                              handleCategoryChange(updatedCategory);
                            }}
                            className="w-full p-2 border rounded"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={category.index}
                            onChange={(e) => {
                              const updatedCategory = {
                                ...category,
                                index: Number.parseInt(e.target.value),
                              };
                              handleCategoryChange(updatedCategory);
                            }}
                            className="w-full p-2 border rounded"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleCategoryDelete(category.id)}
                            disabled={deletingIds.includes(category.id)}
                            className={`px-3 py-1 rounded text-sm ${
                              deletingIds.includes(category.id)
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-red-500 hover:bg-red-600"
                            } text-white`}
                          >
                            {deletingIds.includes(category.id)
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ObjectsPage;
